import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useContext, useEffect, useState } from 'react';
import { useTheme } from 'emotion-theming';
import clsx from 'clsx';
import { Button, Link } from '@jelly/ui';
import { isBrowser } from '@jelly/utils';
import Menu from '@material-ui/core/Menu';
import { withStyles } from '@material-ui/styles';
import { makeStyles } from '@material-ui/core/styles';
import { SiteConfigContext } from '../SiteConfigProvider';
import FeaturedTopics from './FeaturedTopics';
import { MegaPhone } from '../svgs/MegaPhone';
import { trackNavEvent } from './';

var useStyles = function useStyles(_ref) {
  var hubMenu = _ref.hubMenu;
  return makeStyles(function (theme) {
    return {
      AdvertiseLink: {
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        '&:hover': {
          '& svg': {
            '& g': {
              fill: theme.palette.tertiary.main
            }
          }
        }
      },
      AdvertiseWithUs: {
        background: theme.palette.grey[100],
        bottom: '0',
        marginTop: 'auto',
        padding: '0.5rem 0',
        position: 'absolute',
        left: '0',
        textAlign: 'center',
        width: '100%',
        '& svg': {
          margin: 'auto 0.5rem auto 0'
        }
      },
      HubLink: {
        fontFamily: theme.typography.subtitle1.fontFamily,
        lineHeight: '1rem',
        padding: '1rem 0',
        textTransform: 'uppercase',
        width: '100%'
      },
      HubWrapper: {
        display: 'flex',
        width: '100%'
      },
      SeeAllButton: {
        marginBottom: '1rem'
      },
      SubHubElement: {
        margin: '0.5rem 0',
        listStyleType: 'none'
      },
      SubHubElements: {
        alignItems: 'flex-start',
        background: theme.palette.common.white,
        color: theme.palette.primary.main,
        display: 'flex',
        flexDirection: 'column',
        maxWidth: '300px',
        padding: '1.5rem',
        position: 'relative',
        width: '40%'
      },
      SubHubMenuContainer: {
        boxShadow: '0 9px 22px 0 rgba(0, 0, 0, 0.3)',
        display: 'flex',
        height: 'auto',
        maxWidth: '660px',
        width: '660px'
      },
      popover: {
        pointerEvents: 'none'
      }
    };
  }, {
    name: 'subHubMenu'
  });
};

var StyledMenu = withStyles({
  paper: {
    borderRadius: '0',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    height: 'auto',
    width: '660px',
    transition: 'none !important',
    pointerEvents: 'auto'
  }
}, {
  name: 'styledMenu'
})(Menu);
/**
 * Sub Hub Menu, displays a dropdown of sub hub links and featured topics
 *
 * @param {Array} props.featuredTopics :: array of featured topics with url paths and titles
 * @param {String} props.slug :: url slug path for current hub link
 * @param {Array} props.subHubs :: list of subHubs that will display as links under a hub
 * @param {String} props.title :: name of the current hub
 */

export function SubHubMenu(_ref2) {
  var _ref2$featuredTopics = _ref2.featuredTopics,
      featuredTopics = _ref2$featuredTopics === void 0 ? [] : _ref2$featuredTopics,
      slug = _ref2.slug,
      _ref2$subHubs = _ref2.subHubs,
      subHubs = _ref2$subHubs === void 0 ? [] : _ref2$subHubs,
      title = _ref2.title;

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      hubMenu = _useState2[0],
      setHubMenu = _useState2[1];

  var isTouchScreen = isBrowser() && 'ontouchstart' in document.documentElement;

  var _useTheme = useTheme(),
      _useTheme$subHubMenu = _useTheme.subHubMenu,
      buttonVariant = _useTheme$subHubMenu.buttonVariant,
      linkVariant = _useTheme$subHubMenu.linkVariant;

  var _useContext = useContext(SiteConfigContext),
      _useContext$siteConst = _useContext.siteConstants.CONNECT_LINK,
      CONNECT_LINK = _useContext$siteConst === void 0 ? '/connect/' : _useContext$siteConst;

  var handleSubMenuOpen = function handleSubMenuOpen(event) {
    event.preventDefault();
    setHubMenu(event.currentTarget);
  };

  var handleSubMenuClose = function handleSubMenuClose() {
    setHubMenu(null);
  };

  var handleNavClick = function handleNavClick(e, slug) {
    trackNavEvent({
      destination: slug,
      type: 'Hub link'
    });

    if (isTouchScreen) {
      handleSubMenuOpen(e);
    }
  };

  var handleButtonClick = function handleButtonClick(slug) {
    trackNavEvent({
      destination: slug,
      type: 'See All button'
    });
    window.location.href = "/".concat(slug);
  };

  useEffect(function () {
    window.addEventListener('scroll', function () {
      return setHubMenu(null);
    });
    return function () {
      window.removeEventListener('scroll', function () {
        return setHubMenu(null);
      });
    };
  });
  var classes = useStyles({
    hubMenu: hubMenu
  })();
  return /*#__PURE__*/React.createElement("div", {
    className: classes.HubWrapper,
    onMouseEnter: handleSubMenuOpen,
    onMouseLeave: handleSubMenuClose
  }, /*#__PURE__*/React.createElement(Link, {
    "aria-label": title,
    "aria-owns": !!hubMenu ? 'mouse-over-menu' : undefined,
    "aria-haspopup": "true",
    "aria-expanded": !!hubMenu ? 'true' : 'false',
    className: classes.HubLink,
    href: "/".concat(slug),
    onClick: function onClick(e) {
      return handleNavClick(e, slug);
    },
    variant: "light"
  }, title), /*#__PURE__*/React.createElement(StyledMenu, {
    anchorEl: hubMenu,
    getContentAnchorEl: null // corrects menu position placements
    ,
    anchorOrigin: {
      horizontal: 'center',
      vertical: 'bottom'
    },
    disableScrollLock: true,
    keepMounted: true // keep for SEO
    ,
    className: clsx(classes.popover),
    id: "mouse-over-menu",
    MenuListProps: {
      disablePadding: true
    },
    transformOrigin: {
      horizontal: 'center',
      vertical: 'top'
    },
    open: !!hubMenu
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.SubHubMenuContainer
  }, /*#__PURE__*/React.createElement("ul", {
    className: classes.SubHubElements,
    role: "menu"
  }, /*#__PURE__*/React.createElement(Button, {
    "aria-label": "See All ".concat(title),
    className: classes.SeeAllButton,
    onClick: function onClick() {
      return handleButtonClick(slug);
    },
    variant: buttonVariant
  }, "See All"), subHubs.map(function (subHub, i) {
    return /*#__PURE__*/React.createElement("li", {
      className: classes.SubHubElement,
      key: "sub-hub-".concat(i),
      role: "menuitem"
    }, /*#__PURE__*/React.createElement(Link, {
      href: "/".concat(subHub.slug, "/"),
      onClick: function onClick() {
        return trackNavEvent({
          destination: subHub.slug,
          type: 'SubHub link'
        });
      },
      variant: linkVariant
    }, subHub.title));
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.AdvertiseWithUs
  }, /*#__PURE__*/React.createElement(Link, {
    className: classes.AdvertiseLink,
    href: CONNECT_LINK,
    onClick: function onClick() {
      return trackNavEvent({
        destination: slug,
        type: 'AdvertiseWithUs link'
      });
    },
    variant: "tertiary"
  }, /*#__PURE__*/React.createElement(MegaPhone, null), "Advertise With Us!"))), /*#__PURE__*/React.createElement(FeaturedTopics, {
    topics: featuredTopics
  }))));
}